import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { articleApiClient } from '@src/api/company/article/articleApiClient';
import { articleFavoriteApiClient } from '@src/api/company/article/articleFavoriteApiClient';
import { articleTagApiClient } from '@src/api/company/article/articleTagApiClient';
import { bookmarkApiClient } from '@src/api/company/bookmark/bookmarkApiClient';
import { companyEventApiClient } from '@src/api/company/event/eventApiClient';
import { Article } from '@src/api/types/article';
import { Event } from '@src/api/types/event';
import { MediaTag } from '@src/api/types/mediaTag';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useCategory } from '@src/context/CategoryContext';
import { EventProgress } from '@src/enum/EventProgress';
import { RECEPTION_STATUS } from '@src/enum/receptionStatus';
import { useBookmarkLoginPopup } from '@src/hooks/useBookmarkLoginPopup';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import InputSearch from '@src/ui/elements/form/InputSearch';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ArticleItem from './components/ArticleItem';
import ArticleSidebar from './components/ArticleSidebar';
import ArticleSlider from './components/ArticleSlider';

const PER_PAGE = 6;

function ArticleList() {
  const [normalArticles, setNormalArticles] = useState<Article[]>([]);
  const [pickupArticles, setPickupArticles] = useState<Article[]>([]);
  const [popularArticles, setPopularArticles] = useState<Article[]>([]);
  const [newsArticles, setNewsArticles] = useState<Article[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [tags, setTags] = useState<MediaTag[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [dynamicTitle, setDynamicTitle] = useState('');
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { profile } = useCompanyAuth();
  const isLoggedIn = !!profile?.isLogin;
  const { rawMediaCategories, menuMediaCategories } = useCategory();
  const { visiblePopupId, showPopupIfNotLoggedIn, hidePopup, popupRef } = useBookmarkLoginPopup();

  const { renderScrollMobile } = useScrollMobile({
    items: menuMediaCategories,
  });

  const fetchNormalArticles = useCallback(async () => {
    if (!tagsLoaded) {
      return;
    }

    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1', 10);
    const keyword = query.get('keyword');
    const categorySlug = query.get('category_slug');
    const tagSlug = query.get('tag_slug');
    const isInformation = query.get('is_information') === 'true';
    const tag = tags.find((t) => t.slug === tagSlug);
    const category = rawMediaCategories.find((t) => t.slug === categorySlug);

    const params: {
      page: number;
      per_page: number;
      category_slug?: string;
      tag_slug?: string;
      keyword?: string;
      is_information: boolean;
      sort?: string;
    } = {
      page: page,
      per_page: PER_PAGE,
      is_information: isInformation || false,
      sort: 'is_top_display',
    };
    if (category && category.slug) {
      params.category_slug = category.slug;
    } else if (tag && tag.slug) {
      params.tag_slug = tag.slug;
    } else if (keyword) {
      params.keyword = keyword;
    }

    const res = await articleApiClient.index(params);
    handleApiResponse(
      res,
      (data) => {
        setNormalArticles(data?.data || []);
        setPagination(data?.pagination);
      },
      (error) => {
        console.error('記事の取得に失敗しました。', error);
      },
    );
    setSearchValue(params.keyword || '');
    window.scrollTo(0, 0);
  }, [location.search, tagsLoaded]);

  const handleSearchSubmit = () => {
    const query = new URLSearchParams(location.search);
    if (searchValue.trim() === '') {
      query.delete('keyword');
    } else {
      query.set('keyword', searchValue.trim());
    }
    query.delete('category_slug');
    query.delete('tag_slug');
    query.set('page', '1');
    navigate({ search: query.toString() });
  };

  // タイトルの設定
  const updateTitle = () => {
    const query = new URLSearchParams(location.search);
    const categorySlug = query.get('category_slug');
    const tagSlug = query.get('tag_slug');
    const keyword = query.get('keyword');
    const isInformation = query.get('is_information') === 'true';

    let title = 'すべての記事';
    const total = pagination?.total || 0;

    if (categorySlug) {
      const category = rawMediaCategories.find((c) => c.slug === categorySlug);
      if (category) {
        title = total > 0 ? category?.name : `${category?.name} の検索結果 : 0件`;
      }
    } else if (tagSlug) {
      const tag = tags.find((t) => t.slug === tagSlug);
      if (tag) {
        title = total > 0 ? tag.name : `${tag.name} の検索結果 : 0件`;
      }
    } else if (keyword) {
      title = `${keyword} の検索結果 : ${pagination?.total || 0}件`;
    } else if (isInformation) {
      title = 'お知らせ';
    }

    setDynamicTitle(title);
  };

  useEffect(() => {
    if (!pagination) {
      return;
    }
    updateTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  useEffect(() => {
    const fetchTags = async () => {
      setTagsLoaded(false);
      const res = await articleTagApiClient.index();
      handleApiResponse(
        res,
        (data) => {
          const formattedTags = [{ id: 0, name: 'すべてのタグ' } as MediaTag, ...data];
          setTags(formattedTags);
        },
        (error) => console.error('タグの取得に失敗しました。', error),
      );
      setTagsLoaded(true);
    };

    fetchTags();
  }, []);

  useEffect(() => {
    fetchNormalArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, tagsLoaded]);

  useEffect(() => {
    const fetchPickupArticles = async () => {
      const query = new URLSearchParams(location.search);
      const keyword = query.get('keyword');
      const category = query.get('category_slug');
      const tagId = query.get('tag_slug');
      const isInformation = query.get('is_information') === 'true';

      if (keyword || category || tagId || isInformation) {
        setPickupArticles([]);
        return;
      }

      const res = await articleApiClient.index({
        is_pickup: true,
        per_page: 5,
      });

      handleApiResponse(
        res,
        async (data) => {
          const pickupArticlesLength = 5;
          let articles = data?.data || [];

          if (articles.length < pickupArticlesLength) {
            const missingCount = pickupArticlesLength - articles.length;

            // 最新の通常記事を取得
            const normalRes = await articleApiClient.index({
              sort: 'is_top_display',
              direction: 'desc',
              per_page: missingCount,
              is_pickup: false,
            });

            handleApiResponse(normalRes, (normalData) => {
              const normalArticles = normalData?.data || [];
              articles = [...articles, ...normalArticles];
            });
          }

          setPickupArticles(articles);
        },
        (error) => {
          console.error('ピックアップ記事の取得に失敗しました。', error);
        },
      );
    };

    fetchPickupArticles();
  }, [location.search]);

  useEffect(() => {
    const fetchSidebarContents = async () => {
      const popularRes = await articleApiClient.index({
        sort: 'articleAccessCount.access_count',
        direction: 'desc',
        per_page: 3,
      });

      const newsRes = await articleApiClient.index({
        is_information: true,
        sort: 'published_at',
        direction: 'desc',
        per_page: 3,
      });

      const eventRes = await companyEventApiClient.index({
        event_progress: EventProgress.ACTIVE_EVENTS,
        per_page: 3,
        reception_status: RECEPTION_STATUS.NOT_CLOSED,
        page: 1,
      });

      handleApiResponse(
        popularRes,
        (data) => {
          setPopularArticles(data?.data || []);
        },
        (error) => {
          console.error('人気の記事ランキングの取得に失敗しました:', error);
        },
      );

      handleApiResponse(
        newsRes,
        (data) => {
          setNewsArticles(data?.data || []);
        },
        (error) => {
          console.error('お知らせの取得に失敗しました:', error);
        },
      );

      handleApiResponse(
        eventRes,
        (data) => {
          setEvents(data?.data || []);
        },
        (error) => {
          console.error('直近のイベントの取得に失敗しました:', error);
        },
      );
    };

    fetchSidebarContents();
  }, []);

  const handleFavoriteToggle = async (articleId: number) => {
    const prevNormal = normalArticles;
    const prevPickup = pickupArticles;

    const optimisticUpdate = (articles: Article[]) =>
      articles.map((a) => {
        if (a.id !== articleId) {
          return a;
        }
        const currentFavorite = a.favorite ?? { is_favorite: false, favorites_count: 0 };
        return {
          ...a,
          favorite: {
            is_favorite: !currentFavorite.is_favorite,
            favorites_count: currentFavorite.is_favorite
              ? currentFavorite.favorites_count - 1
              : currentFavorite.favorites_count + 1,
          },
        };
      });

    setNormalArticles((prev) => optimisticUpdate(prev));
    setPickupArticles((prev) => optimisticUpdate(prev));

    const res = await articleFavoriteApiClient.toggle(articleId);
    handleApiResponse(res, (data) => {
      const confirmUpdate = (articles: Article[]) =>
        articles.map((a) => (a.id === articleId ? { ...a, favorite: data } : a));
      setNormalArticles((prev) => confirmUpdate(prev));
      setPickupArticles((prev) => confirmUpdate(prev));
    });
    if (res.result !== 'success' && res.result !== 'aborted') {
      setNormalArticles(prevNormal);
      setPickupArticles(prevPickup);
    }
  };

  const handleBookmarkToggle = async (articleId: number) => {
    const updateArticles = (articles: Article[]) =>
      articles.map((a) => (a.id === articleId ? { ...a, is_bookmarked: !a.is_bookmarked } : a));

    setNormalArticles((prev) => updateArticles(prev));
    setPickupArticles((prev) => updateArticles(prev));

    const res = await bookmarkApiClient.toggle(articleId);
    handleApiResponse(res, (data) => {
      const confirmUpdate = (articles: Article[]) =>
        articles.map((a) => (a.id === articleId ? { ...a, is_bookmarked: data.is_bookmarked } : a));
      setNormalArticles((prev) => confirmUpdate(prev));
      setPickupArticles((prev) => confirmUpdate(prev));
    });
  };

  return (
    <div className='h-full'>
      <main className='inline-block w-[calc(100%-240px)] px-6 pb-16 pt-10 lg:block lg:w-full lg:pt-6'>
        <div className='flex items-center justify-between lg:flex-col lg:items-start'>
          <h1 className='overflow-wrap-anywhere text-xl font-semibold lg:mt-6'>{dynamicTitle}</h1>
          <div className='mt-4 hidden max-w-full lg:-order-1 lg:block'>
            <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
          </div>
          <div className='w-[250px] min-w-[250px] lg:-order-2 lg:w-full'>
            <InputSearch
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              onSubmit={handleSearchSubmit}
              onClear={() => {
                setSearchValue('');
                navigate({ search: '' });
              }}
            />
          </div>
        </div>

        {pickupArticles.length > 0 && (
          <div className='mt-10 lg:mt-4'>
            <ArticleSlider
              articles={pickupArticles.map((article) => ({
                to: `/articles/${article.id}`,
                title: article.title,
                content: article.content,
                date: article.published_at,
                category: {
                  id: article?.category?.id ?? 0,
                  name: article?.category?.name ?? '',
                  slug: article?.category?.slug ?? '',
                },
                tags: (article?.tags ?? []).map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  slug: tag.slug ?? '',
                })),
                thumbnail: article.thumbnail_url || PhotoGrayIcon,
                isBookmarked: article.is_bookmarked ?? false,
                isLiked: article.favorite?.is_favorite,
                likesCount: article.favorite?.favorites_count,
                isMemberLimited: article.is_member_limited,
                ...(isLoggedIn
                  ? { onFavoriteToggle: () => handleFavoriteToggle(article.id) }
                  : {
                      onFavoriteClickWhenLoggedOut: () =>
                        showPopupIfNotLoggedIn(`favorite-pickup-${article.id}`),
                      favoritePopupId: `favorite-pickup-${article.id}`,
                    }),
                ...(isLoggedIn
                  ? { onBookmarkToggle: () => handleBookmarkToggle(article.id) }
                  : {
                      onBookmarkClickWhenLoggedOut: () =>
                        showPopupIfNotLoggedIn(`pickup-${article.id}`),
                      bookmarkPopupId: `pickup-${article.id}`,
                    }),
              }))}
              visiblePopupId={!isLoggedIn ? visiblePopupId : undefined}
              popupRef={!isLoggedIn ? popupRef : undefined}
              onPopupClose={!isLoggedIn ? hidePopup : undefined}
            />
          </div>
        )}

        <div className='mt-8 grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-1'>
          {normalArticles.length > 0 &&
            normalArticles.map((article, index) => (
              <ArticleItem
                key={index}
                to={`/articles/${article.id}`}
                title={article.title}
                content={article.content}
                date={article.published_at}
                category={{
                  id: article?.category?.id ?? 0,
                  name: article?.category?.name ?? '',
                  slug: article?.category?.slug ?? '',
                }}
                tags={(article?.tags ?? []).map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  slug: tag.slug ?? '',
                }))}
                thumbnail={article.thumbnail_url || PhotoGrayIcon}
                isBookmarked={article.is_bookmarked ?? false}
                isLiked={article.favorite?.is_favorite}
                isMemberLimited={article.is_member_limited}
                lockIconMdClassName='md:mt-[3px] md:size-[15px]'
                likesCount={article.favorite?.favorites_count}
                onFavoriteToggle={isLoggedIn ? () => handleFavoriteToggle(article.id) : undefined}
                onFavoriteClickWhenLoggedOut={
                  !isLoggedIn
                    ? () => showPopupIfNotLoggedIn(`favorite-normal-${article.id}`)
                    : undefined
                }
                favoritePopupId={`favorite-normal-${article.id}`}
                onBookmarkToggle={isLoggedIn ? () => handleBookmarkToggle(article.id) : undefined}
                onBookmarkClickWhenLoggedOut={
                  !isLoggedIn ? () => showPopupIfNotLoggedIn(`normal-${article.id}`) : undefined
                }
                bookmarkPopupId={`normal-${article.id}`}
                visiblePopupId={!isLoggedIn ? visiblePopupId : undefined}
                popupRef={!isLoggedIn ? popupRef : undefined}
                onPopupClose={!isLoggedIn ? hidePopup : undefined}
              />
            ))}
        </div>

        {pagination && pagination.total > PER_PAGE && (
          <div className='mt-16 lg:flex lg:justify-center'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={(page) => {
                const query = new URLSearchParams(location.search);
                query.set('page', page.toString());
                navigate({ search: query.toString() });
              }}
            />
          </div>
        )}
      </main>

      <ArticleSidebar
        popularArticles={popularArticles.map((article) => ({
          to: `/articles/${article.id}`,
          title: article.title,
        }))}
        newsArticles={newsArticles.map((article) => ({
          to: `/articles/${article.id}`,
          title: article.title,
          date: article.published_at,
        }))}
        events={events.map((event) => ({
          to: `/events/${event.id}`,
          title: event.title,
          date: event.event_start_at.split(' ')[0],
        }))}
        onNavigateToNews={() => {
          setSearchValue('');
          navigate('?is_information=true&page=1');
        }}
        onNavigateToEvents={() => {
          navigate('/events');
        }}
      />
    </div>
  );
}

export default ArticleList;
